import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref as dbRef, get, push } from "firebase/database";
import { uploadImage } from "../utils/uploadImage";
import { useAuth } from "../context/AuthContext";
import { Search, Plus, X, Camera, AlertCircle, MapPin, Calendar, ArrowLeft } from "lucide-react";

function ItemCard({ item }) {
  const isLost = item.type === "lost";
  const waMsg = encodeURIComponent(
    isLost
      ? `Hi! I think I found your lost item "${item.itemName}" which you posted on CampusSwap. Please contact me.`
      : `Hi! I'm looking for my lost item "${item.itemName}" - I saw you found something similar on CampusSwap.`
  );
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
      <div className="flex items-start gap-3">
        {item.imageUrl && (
          <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
            <img src={item.imageUrl} alt={item.itemName} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isLost ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
              {isLost ? "LOST" : "FOUND"}
            </span>
            {item.status === "resolved" && (
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Resolved</span>
            )}
          </div>
          <p className="font-semibold text-gray-800 text-sm">{item.itemName}</p>
          <div className="flex items-center gap-1 mt-1">
            <MapPin size={11} className="text-gray-400" />
            <p className="text-xs text-gray-500">{item.location}</p>
          </div>
          <div className="flex items-center gap-1">
            <Calendar size={11} className="text-gray-400" />
            <p className="text-xs text-gray-500">{item.date}</p>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">{item.postedByName} · {item.college}</p>
        </div>
      </div>
      {item.reward && (
        <div className="flex items-center gap-1.5 mt-2 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-1.5">
          <span className="text-sm">🎁</span>
          <p className="text-xs font-semibold text-yellow-700">Reward: {item.reward}</p>
        </div>
      )}
      {item.description && <p className="text-xs text-gray-500 mt-2 line-clamp-2">{item.description}</p>}
      {item.status !== "resolved" && (
        <a href={`https://wa.me/91${item.whatsapp?.replace(/\D/g, "")}?text=${waMsg}`}
          target="_blank" rel="noreferrer"
          className="mt-3 block text-center bg-green-500 text-white py-2 rounded-xl text-sm font-semibold hover:bg-green-600">
          {isLost ? "I Found This!" : "I Lost This!"}
        </a>
      )}
    </div>
  );
}

export default function LostFound() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("lost");
  const [showForm, setShowForm] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    type: "lost", itemName: "", description: "", location: "", date: "", whatsapp: userProfile?.phone || "", reward: ""
  });

  useEffect(() => { fetchItems(); }, []);

  async function fetchItems() {
    setLoading(true);
    try {
      const snap = await get(dbRef(db, "lostfound"));
      if (snap.exists()) {
        const all = Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }));
        setItems(all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } else {
        setItems([]);
      }
    } catch (e) {  }
    setLoading(false);
  }

  function handleChange(e) { setForm({ ...form, [e.target.name]: e.target.value }); }

  function handleImage(e) {
    const f = e.target.files[0];
    if (f) { setImageFile(f); setPreview(URL.createObjectURL(f)); }
  }

  async function handlePost(e) {
    e.preventDefault();
    setError("");
    if (!form.itemName?.trim() || form.itemName.trim().length < 3) return setError("Item name must be at least 3 characters.");
    if (!imageFile) return setError("Please add a photo — it helps identify the item much faster.");
    if (!form.location.trim()) return setError("Please enter the location.");
    if (!form.date.trim()) return setError("Please enter the date.");
    if (!form.whatsapp.trim() || !/^[6-9]\d{9}$/.test(form.whatsapp.replace(/\s/g, "")))
      return setError("Enter a valid 10-digit WhatsApp number.");
    setPosting(true);
    try {
      let imageUrl = null;
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }
      await push(dbRef(db, "lostfound"), {
        type: form.type,
        itemName: form.itemName.trim(),
        description: form.description.trim(),
        location: form.location.trim(),
        date: form.date.trim(),
        whatsapp: form.whatsapp.trim(),
        reward: form.type === "lost" ? form.reward.trim() : "",
        imageUrl,
        postedBy: currentUser.uid,
        postedByName: userProfile?.name || "Student",
        college: userProfile?.college || "",
        status: "active",
        createdAt: new Date().toISOString(),
      });
      setForm({ type: "lost", itemName: "", description: "", location: "", date: "", whatsapp: userProfile?.phone || "", reward: "" });
      setImageFile(null); setPreview(null);
      setShowForm(false);
      fetchItems();
    } catch (err) { setError("Failed to post. Try again."); }
    setPosting(false);
  }

  const filtered = items.filter(i => i.type === activeTab);

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-r from-orange-500 to-red-500 px-4 pt-4 pb-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-full hover:bg-white/20"><ArrowLeft size={20} /></button>
            <div>
              <h1 className="text-xl font-bold">Lost & Found</h1>
              <p className="text-orange-100 text-xs mt-0.5">Report lost items · Claim found ones</p>
            </div>
          </div>
          <button onClick={() => setShowForm(true)}
            className="bg-white text-orange-600 px-3 py-1.5 rounded-xl font-semibold text-sm flex items-center gap-1">
            <Plus size={16} /> Post
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 -mt-3 mb-4">
        <div className="bg-white rounded-xl shadow-sm p-1 flex">
          <button onClick={() => setActiveTab("lost")}
            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition ${activeTab === "lost" ? "bg-red-500 text-white" : "text-gray-600"}`}>
            Lost Items ({items.filter(i => i.type === "lost").length})
          </button>
          <button onClick={() => setActiveTab("found")}
            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition ${activeTab === "found" ? "bg-green-500 text-white" : "text-gray-600"}`}>
            Found Items ({items.filter(i => i.type === "found").length})
          </button>
        </div>
      </div>

      <div className="px-4 space-y-3">
        {loading ? (
          [1,2,3].map(i => <div key={i} className="h-28 bg-white rounded-xl animate-pulse" />)
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl">
            <Search size={48} className="mx-auto mb-3 text-gray-200" />
            <p className="text-gray-500 font-medium">No {activeTab} items posted</p>
            <button onClick={() => setShowForm(true)} className="mt-4 bg-orange-500 text-white px-6 py-2.5 rounded-xl font-semibold text-sm">
              Post a Report
            </button>
          </div>
        ) : filtered.map(item => <ItemCard key={item.id} item={item} />)}
      </div>

      {/* Post Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-end">
          <div className="bg-white rounded-t-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-4 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-800 text-lg">Report Lost / Found Item</h3>
              <button onClick={() => { setShowForm(false); setError(""); }}><X size={24} /></button>
            </div>
            <form onSubmit={handlePost} className="p-4 space-y-3 pb-24">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                  <AlertCircle size={16} />{error}
                </div>
              )}
              <div className="flex bg-gray-100 rounded-xl p-1">
                <button type="button" onClick={() => setForm({...form, type: "lost"})}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold ${form.type === "lost" ? "bg-red-500 text-white" : "text-gray-600"}`}>
                  I Lost Something
                </button>
                <button type="button" onClick={() => setForm({...form, type: "found"})}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold ${form.type === "found" ? "bg-green-500 text-white" : "text-gray-600"}`}>
                  I Found Something
                </button>
              </div>

              <div onClick={() => document.getElementById("lf-img").click()}
                className="border-2 border-dashed border-orange-300 rounded-xl h-28 flex items-center justify-center cursor-pointer overflow-hidden">
                {preview ? <img src={preview} alt="p" className="h-full w-full object-cover" />
                  : <div className="text-orange-400 text-center"><Camera size={24} className="mx-auto mb-1" /><p className="text-xs">Photo (optional)</p></div>}
              </div>
              <input id="lf-img" type="file" accept="image/*" onChange={handleImage} className="hidden" />

              <input name="itemName" value={form.itemName} onChange={handleChange} required
                placeholder="Item name (e.g. Blue Water Bottle, ID Card)"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-orange-400" />

              <input name="location" value={form.location} onChange={handleChange} required
                placeholder={form.type === "lost" ? "Where was it last seen?" : "Where did you find it?"}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-orange-400" />

              <input name="date" value={form.date} onChange={handleChange} required
                placeholder="Date (e.g. 20 March, yesterday)"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-orange-400" />

              <textarea name="description" value={form.description} onChange={handleChange} rows={2}
                placeholder="Any identifying details, color, brand..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-orange-400 resize-none" />

              {form.type === "lost" && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1">🎁 Reward (optional)</p>
                  <input name="reward" value={form.reward} onChange={handleChange}
                    placeholder="e.g. Treat at canteen, Thank you card, Chocolate box"
                    className="w-full border border-yellow-300 bg-yellow-50 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-yellow-400" />
                  <p className="text-xs text-gray-400 mt-1">Non-monetary gesture to thank the finder</p>
                </div>
              )}

              <input name="whatsapp" type="tel" value={form.whatsapp} onChange={handleChange} required
                placeholder="Your WhatsApp number *"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-orange-400" />

              <button type="submit" disabled={posting}
                className="w-full bg-orange-500 text-white py-3.5 rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-50">
                {posting ? "Posting..." : "Post Report"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
